import React, { useState, useCallback } from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { Text } from 'rebass'
import { Contract } from 'web3-eth-contract'
import { Plus } from 'react-feather'

import { getBalanceNumber } from '../../../utils/bakery/formatBalance'
import { ButtonPrimary } from '../../../components/Button'
import Value from '../../../components/Value'
import WithdrawModal from './WithdrawModal'
import DepositModal from './DepositModal'

// hooks
import useYam from '../../../hooks/bakery/useYam'
import useStakedBalance from '../../../hooks/bakery/useStakedBalance'
import useAllowance from '../../../hooks/bakery/useAllowance'
import useApprove from '../../../hooks/bakery/useApprove'
import useModal from '../../../hooks/bakery/useModal'
import useTokenBalance from '../../../hooks/bakery/useTokenBalance'
import useStake from '../../../hooks/bakery/useStake'
import useUnstake from '../../../hooks/bakery/useUnstake'

import { MINTTOKEN } from '../../../constants/bakery'
import { poolMasterContract } from '../../../userType/poolMasterContract'

const AmountElement = styled(Text)<{ isTen: boolean }>`
  color: ${({ theme }) => theme.bsText4};
  & > div > span {
    color: ${({ isTen }) => (isTen ? '#303068' : '')};
  }
`
const EarnBtn = styled(ButtonPrimary)<{ isTen: boolean }>`
  position: relative;
  flex: 1;
  font-family: Baloo Da;
  height: 48px;
  padding: 0 16px;
  background-color: ${({ isTen }) => (isTen ? '#DA4550' : '')};
  border: ${({ isTen }) => (isTen ? '2px solid #2A3567' : '')};
  &:hover {
    background-color: ${({ isTen }) => (isTen ? '#DA4550' : '')};
  }
  &:disabled {
    background-color: ${({ isTen }) => (isTen ? '#A3A3A3' : '')};
  }
`
const IconBtn = styled.img`
  position: absolute;
`
const AddBtn = styled(ButtonPrimary)<{ isTen: boolean }>`
  flex-shrink: 0;
  font-family: Baloo Da;
  width: 48px;
  height: 48px;
  margin-left: 16px;
  padding: 0;
  background-color: ${({ isTen }) => (isTen ? '#DA4550' : '')};
  &:hover {
    background-color: ${({ isTen }) => (isTen ? '#DA4550' : '')};
  }
`
const BtnWrpper = styled.div<{ isTen: boolean }>`
  display: flex;
  align-items: center;
  margin-top: ${({ isTen }) => (isTen ? '14px' : '40px')};
`
const StyledPlusIcon = styled(Plus)`
  height: 20px;
  width: 20px;

  > * {
    stroke: ${({ theme }) => theme.primaryText1};
  }
`

interface StakeProps {
  name: string
  pid: number
  lpContract: Contract
  lpTokenAddress: string
  lpTokenName: string
  earnTokenName: string
}

// eslint-disable-next-line
const Stake: React.FC<StakeProps> = ({ name, lpContract, lpTokenAddress, pid, lpTokenName, earnTokenName }) => {
  const [requestedApproval, setRequestedApproval] = useState(false)
  const sushi = useYam()
  let masterContract: Contract = poolMasterContract(sushi, earnTokenName)
  
  const stakedBalance = useStakedBalance(lpTokenAddress, masterContract)
  const allowance = useAllowance(lpContract, masterContract)

  const { onApprove } = useApprove(lpContract, masterContract)
  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      const txHash = await onApprove()
      // user rejected tx or didn't go thru
      if (!txHash) {
        setRequestedApproval(false)
      }
    } catch (e) {
      console.log(e)
    }
  }, [onApprove, setRequestedApproval])

  // eslint-disable-next-line
  const tokenBalance = useTokenBalance(lpContract.options.address)
  const { onStake } = useStake(lpTokenAddress, masterContract)
  const { onUnstake } = useUnstake(lpTokenAddress, masterContract)
  const [onPresentWithdraw] = useModal(
    <WithdrawModal max={stakedBalance} onConfirm={onUnstake} tokenName={lpTokenName} />
  )

  const [onPresentDeposit] = useModal(<DepositModal max={tokenBalance} onConfirm={onStake} tokenName={lpTokenName} />)

  return (
    <>
      <AmountElement isTen={earnTokenName === MINTTOKEN.TEN} fontSize={36} fontWeight={700}>
        <Value value={getBalanceNumber(stakedBalance)} />
      </AmountElement>
      {earnTokenName === MINTTOKEN.TEN ? (
        <Text fontSize={14} color={'#2A3567'} fontWeight={600}>
          {lpTokenName} Tokens Staked
        </Text>
      ) : (
        <Text fontSize={14} fontFamily={'Baloo Da'} fontWeight={600}>
          {lpTokenName} Tokens Staked
        </Text>
      )}
      <BtnWrpper isTen={earnTokenName === MINTTOKEN.TEN}>
        {!allowance.toNumber() ? (
          earnTokenName === MINTTOKEN.TEN ? (
            <EarnBtn isTen={earnTokenName === MINTTOKEN.TEN} disabled={requestedApproval} onClick={handleApprove}>
              <IconBtn style={{ left: '-20px' }} src="/images/bakery/icon_red_btn.svg"></IconBtn>
              <Text fontSize={14} fontWeight={700}>{`Approve ${lpTokenName}`}</Text>
              <IconBtn style={{ right: '-8px', bottom: '-15px' }} src="/images/bakery/icon_yuanbao.svg"></IconBtn>
            </EarnBtn>
          ) : (
            <EarnBtn isTen={earnTokenName === MINTTOKEN.TEN} disabled={requestedApproval} onClick={handleApprove}>
              <Text fontSize={14} fontWeight={700}>{`Approve ${lpTokenName}`}</Text>
            </EarnBtn>
          )
        ) : (
          <>
            {![MINTTOKEN.TEN].includes(earnTokenName) ? (
              <EarnBtn
                isTen={earnTokenName === MINTTOKEN.TEN}
                disabled={stakedBalance.eq(new BigNumber(0))}
                onClick={onPresentWithdraw}
              >
                <Text fontSize={14} fontWeight={700}>
                  Unstake
                </Text>
              </EarnBtn>
            ) : (
              <EarnBtn
                isTen={earnTokenName === MINTTOKEN.TEN}
                disabled={stakedBalance.eq(new BigNumber(0))}
                onClick={onPresentWithdraw}
              >
                <IconBtn style={{ left: '-20px' }} src="/images/bakery/icon_red_btn.svg"></IconBtn>
                <Text fontSize={14} fontWeight={700}>
                  Unstake
                </Text>
                <IconBtn style={{ right: '-8px', bottom: '-15px' }} src="/images/bakery/icon_yuanbao.svg"></IconBtn>
              </EarnBtn>
            )}
            <AddBtn
              isTen={earnTokenName === MINTTOKEN.TEN}
              onClick={onPresentDeposit}
              disabled={name === 'Pet Airdrop'}
            >
              <StyledPlusIcon />
            </AddBtn>
          </>
        )}
      </BtnWrpper>
    </>
  )
}

export default Stake
